import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import AnalogyLogo from "@/components/AnalogyLogo";
import { toast } from "sonner";
import { Camera, Loader2, Trash2, Bell } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loginSummary, setLoginSummary] = useState(null);
  const [pendingDest, setPendingDest] = useState(null);

  // Branding hero (single image, public endpoint).
  const [heroUrl, setHeroUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/branding/login-hero").then(({ data }) => {
      if (data?.url) setHeroUrl(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
    }).catch(() => { /* ok — endpoint just returns null when nothing's set */ });
  }, []);

  const isAdmin = user?.role === "founder";

  const onPickHero = () => fileRef.current?.click();
  const onHeroFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = "";
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/branding/login-hero", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHeroUrl(`${process.env.REACT_APP_BACKEND_URL}${data.url}?t=${Date.now()}`); // cache-bust
      toast.success("Hero image updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };
  const onClearHero = async () => {
    if (!window.confirm("Remove the current hero image?")) return;
    try { await api.delete("/admin/branding/login-hero"); setHeroUrl(null); toast.success("Hero cleared"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      let dest;
      if (u.role === "vendor") {
        dest = (u.status && u.status !== "active") ? "/vendor/onboarding" : "/vendor";
      } else if (u.role === "client") {
        dest = "/client";
      } else {
        dest = loc.state?.from?.pathname || "/";
      }
      // Feb 2026 — Notification Center: show what happened since last login
      // before navigating away, for internal roles only.
      const since = (u.new_since_last_login || []).filter((n) => n && n.subject);
      if (since.length > 0 && u.role !== "vendor" && u.role !== "client") {
        setLoginSummary(since);
        setPendingDest(dest);
      } else {
        nav(dest, { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const dismissSummary = () => {
    setLoginSummary(null);
    if (pendingDest) nav(pendingDest, { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8F9FA]">
      {/* Left brand panel — hero photo when set, else the original gradient. */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0F172A] text-slate-100 relative overflow-hidden">
        {/* Background image layer */}
        {heroUrl && (
          <div className="absolute inset-0">
            <img src={heroUrl} alt="Studio hero" className="w-full h-full object-cover" data-testid="login-hero-img" />
            {/* Dark gradient overlay so the white copy on top stays legible. */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A]/85 via-[#0F172A]/55 to-[#0F172A]/85" />
          </div>
        )}

        <div className="relative z-10">
          <AnalogyLogo size={28} className="text-white" />
          <div className="label-overline text-slate-400 mt-2">Studio Operating System</div>
        </div>
        <div className="max-w-md relative z-10">
          <div className="label-overline text-[#F54800] mb-4">Internal portal</div>
          <h2 className="font-display text-4xl font-bold text-white leading-tight tracking-tight">
            From sketch to factory floor — one operating system for the studio.
          </h2>
          <p className="text-slate-300 mt-6 leading-relaxed">
            Projects, milestones, timesheets, finance, weekly client comms and chat.
            Built for design teams shipping physical products.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-6 relative z-10">
          {[
            { k: "Business Units", v: "4" },
            { k: "Active Projects", v: "7+" },
            { k: "Modules", v: "16" },
          ].map((s) => (
            <div key={s.k}>
              <div className="font-display text-3xl font-bold text-white">{s.v}</div>
              <div className="label-overline text-slate-400 mt-1">{s.k}</div>
            </div>
          ))}
        </div>
        {!heroUrl && (
          <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-[#F54800]/20 blur-3xl pointer-events-none" />
        )}

        {/* Admin-only inline hero editor — only renders when an admin is signed in. */}
        {isAdmin && (
          <div className="absolute top-4 right-4 z-20 flex items-center gap-2" data-testid="login-hero-admin">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onHeroFile} data-testid="login-hero-input" />
            <Button
              size="sm"
              onClick={onPickHero}
              disabled={uploading}
              className="rounded-sm bg-white/10 hover:bg-white/20 text-white backdrop-blur border border-white/20"
              data-testid="login-hero-upload"
            >
              {uploading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Camera size={13} className="mr-1.5" />}
              {heroUrl ? "Replace photo" : "Add photo"}
            </Button>
            {heroUrl && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onClearHero}
                className="rounded-sm text-white/80 hover:bg-white/10 hover:text-white"
                data-testid="login-hero-clear"
                title="Remove hero photo"
              >
                <Trash2 size={13} />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Login form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="w-full max-w-sm bg-white card-flat p-8"
        >
          <div className="label-overline mb-3">Sign in</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Welcome back</h1>
          <p className="text-slate-500 text-sm mt-2">Use your studio email to access the portal.</p>

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="label-overline">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                placeholder="you@analogydesign.co"
                className="mt-2 rounded-sm"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="label-overline">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                placeholder="••••••••"
                className="mt-2 rounded-sm"
                required
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">{error}</div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
            <div className="text-center">
              <Link to="/forgot-password" className="text-xs text-slate-500 hover:text-[#F54800] hover:underline" data-testid="login-forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </div>
        </form>
      </div>

      {/* Feb 2026 — Notification Center login summary: what changed since last login */}
      <Dialog open={!!loginSummary} onOpenChange={(v) => !v && dismissSummary()}>
        <DialogContent className="rounded-sm max-w-md" data-testid="login-summary-modal">
          <DialogHeader>
            <DialogTitle className="font-display inline-flex items-center gap-2"><Bell size={17} className="text-[#F54800]" /> Since you were last here</DialogTitle>
            <DialogDescription>{loginSummary?.length} update{loginSummary?.length !== 1 ? "s" : ""} on your requests and approvals.</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-2 mt-2">
            {(loginSummary || []).map((n) => (
              <div key={n.id} className="border border-slate-200 rounded-sm p-3" data-testid={`login-summary-item-${n.id}`}>
                <div className="text-sm font-medium text-slate-900">{n.subject}</div>
                {n.message && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={dismissSummary} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid="login-summary-dismiss">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
